/**
 * Account Dashboard Page
 * Main account management page showing subscription info and quick actions
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Calendar, CreditCard, ArrowRight, 
  FileText, AlertCircle, Crown, Clock, X, Loader2
} from 'lucide-react';
import AccountHeader from '../../components/account/AccountHeader';
import { useAccountAuth } from '../../contexts/AccountAuthContext';
import { API_BASE_URL } from '../../config/api';

// Mock billing history - TODO: Replace with API call
const AccountDashboard = () => {
  const navigate = useNavigate();
  const { user, subscription, defaultPaymentMethod, isAuthenticated, isLoading, refreshUserData } = useAccountAuth();
  const [cancellingScheduled, setCancellingScheduled] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/app/account');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleCancelScheduledChange = async () => {
    setCancellingScheduled(true);
    try {
      const email = sessionStorage.getItem('account_email') || '';
      const pwd = sessionStorage.getItem('account_pwd') || '';
      
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'cancel_scheduled',
          email,
          pwd
        })
      });
      
      const result = await response.json();
      if (result.success) {
        await refreshUserData();
      } else {
        alert(result.message || 'Failed to cancel scheduled change');
      }
    } catch (err) {
      console.error('Error cancelling scheduled change:', err);
      alert('Failed to cancel scheduled change');
    } finally {
      setCancellingScheduled(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Build address string
  const buildAddress = () => {
    const parts = [];
    if (user?.addressLine1) parts.push(user.addressLine1);
    if (user?.addressLine2) parts.push(user.addressLine2);
    const cityStateZip = [user?.city, user?.state, user?.zipcode].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join(', ');
  };

  const address = buildAddress();
  const memberSinceFormatted = formatDate(user?.memberSince);

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="My Account" showBack={false} />
      
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Business Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-accent/20 flex items-center justify-center">
              <User className="w-7 h-7 text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{user?.businessName || user?.name}</h2>
              {address && (
                <p className="text-gray-400 text-sm mt-0.5">{address}</p>
              )}
              {memberSinceFormatted && (
                <p className="text-sm text-gray-500 mt-1">
                  <Calendar className="w-3.5 h-3.5 inline mr-1" />
                  Member since {memberSinceFormatted}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10"
        >
          <h3 className="text-sm font-medium text-gray-400 mb-4">CURRENT PLAN</h3>
          
          <div className="bg-dark-200 rounded-xl p-4 border border-accent/30">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-accent" />
                <span className="text-lg font-semibold text-white">{subscription?.planName}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-sm text-emerald-400 capitalize">{subscription?.status}</span>
              </div>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              Billing Cycle: <span className="text-white capitalize">{subscription?.billingCycle}</span>
            </p>

            <div className="h-px bg-white/10 mb-4" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Next Payment</p>
                <p className="text-white font-medium">{formatDate(subscription?.nextPaymentDate)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Amount Due</p>
                <p className="text-white font-medium">${subscription?.nextPaymentAmount?.toFixed(2)}</p>
              </div>
            </div>

            {subscription?.cancelAtPeriodEnd && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-500 font-medium">Cancellation Scheduled</p>
                    <p className="text-xs text-yellow-500/70">Your plan will be cancelled on {formatDate(subscription?.currentPeriodEnd)}</p>
                  </div>
                </div>
              </div>
            )}

            {subscription?.scheduledChange && (
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-400 font-medium">Plan Change Scheduled</p>
                      <p className="text-xs text-blue-400/70">
                        Your plan will change to {subscription.scheduledChange.planName} on {formatDate(subscription.scheduledChange.scheduledAt)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancelScheduledChange}
                    disabled={cancellingScheduled}
                    className="ml-3 p-1 text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                    title="Cancel scheduled change"
                  >
                    {cancellingScheduled ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-dark-100 rounded-2xl p-5 border border-white/10"
        >
          <h3 className="text-sm font-medium text-gray-400 mb-4">QUICK ACTIONS</h3>
          
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              onClick={() => navigate('/app/account/plans', { state: { fromPage: 'dashboard' } })}
              className="p-4 bg-dark-200 rounded-xl border border-white/10 hover:border-accent/30 transition-all text-left group"
            >
              <FileText className="w-6 h-6 text-accent mb-2" />
              <p className="text-white font-medium">Change Plan</p>
              <p className="text-xs text-gray-500">Upgrade/Downgrade</p>
              <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-accent mt-2 transition-colors" />
            </button>

            <button
              onClick={() => navigate('/app/account/payment', { state: { fromPage: 'dashboard' } })}
              className="p-4 bg-dark-200 rounded-xl border border-white/10 hover:border-accent/30 transition-all text-left group"
            >
              <CreditCard className="w-6 h-6 text-accent mb-2" />
              <p className="text-white font-medium">Payment Method</p>
              <p className="text-xs text-gray-500">
                {defaultPaymentMethod ? `•••• ${defaultPaymentMethod.last4}` : 'Add card'}
              </p>
              <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-accent mt-2 transition-colors" />
            </button>
          </div>

          <button
            onClick={() => navigate('/app/account/cancel', { state: { fromPage: 'dashboard' } })}
            className="w-full p-4 bg-dark-200 rounded-xl border border-white/10 hover:border-red-500/30 transition-all text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Cancel Membership</p>
                <p className="text-xs text-gray-500">We'll be sad to see you go</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-red-400 transition-colors" />
            </div>
          </button>
        </motion.div>

        {/* Business Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => navigate('/app/account/business-profile', { state: { fromPage: 'dashboard' } })}
            className="w-full p-4 bg-dark-100 rounded-2xl border border-white/10 hover:border-accent/30 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-accent" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">Business Profile</p>
                  <p className="text-xs text-gray-500">Name, category & contact info</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-accent transition-colors" />
            </div>
          </button>
        </motion.div>

        {/* My Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => navigate('/app/account/my-subscription', { state: { fromPage: 'dashboard' } })}
            className="w-full p-4 bg-dark-100 rounded-2xl border border-white/10 hover:border-accent/30 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-accent" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">My Subscription</p>
                  <p className="text-xs text-gray-500">Plan details, usage & features</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-accent transition-colors" />
            </div>
          </button>
        </motion.div>

        {/* View Payment History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <button
            onClick={() => navigate('/app/account/payment-history', { state: { fromPage: 'dashboard' } })}
            className="w-full p-4 bg-dark-100 rounded-2xl border border-white/10 hover:border-accent/30 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-accent" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">View Payment History</p>
                  <p className="text-xs text-gray-500">Invoices, receipts & billing details</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-accent transition-colors" />
            </div>
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountDashboard;
